import Flex from 'components/ui/Flex';
import SearchInput from './SearchInput';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    clearSearchOrders,
    selectedOrderSet,
    orderThunks,
    selectOrderSearchFilters,
    setOrderSearchFilters,
    selectIsOrderTypeExcluded,
} from 'store/reducers/orderSlice';
import { clearReturnSlip as clearReturnSlipSet } from 'store/reducers/returnSlice';
import useAppDispatch from 'hooks/useAppDispatch';
import { useNavigate } from 'react-router-dom';
import useDebounce from 'hooks/useDebounce';
import useAppSelector from 'hooks/useAppSelector';
import { selectOrigin } from 'store/reducers/navigationSlice';

type SearchProps = {
    placeholder?: string;
};

const Search = ({ placeholder }: SearchProps) => {
    const dispatch = useAppDispatch();

    const [query, setQuery] = useState('');

    const inputContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const navigate = useNavigate();

    const origin = useAppSelector(selectOrigin);
    const { from, to } = useAppSelector(selectOrderSearchFilters);
    const isOrderTypeExcluded = useAppSelector(selectIsOrderTypeExcluded);

    const trimmed = query.trim();

    const handleSearch = useCallback(() => {
        if (!trimmed) return;

        dispatch(setOrderSearchFilters({ query: trimmed }));

        dispatch(
            orderThunks.searchOrders({
                searchString: trimmed,
                from: from?.toISOString(),
                to: to?.toISOString(),
            }),
        )
            .unwrap()
            .then((data) => {
                const orderNumberStr = String(data[0]?.orderNumber ?? '').trim();
                const order = data[0];
                if (data.length === 1 && orderNumberStr === trimmed && !isOrderTypeExcluded(order)) {
                    dispatch(clearReturnSlipSet());
                    dispatch(selectedOrderSet(order));

                    navigate(`/search/order/${order.orderNumber}`, {
                        state: {
                            orderIdFromPath: order.orderNumber,
                            origin: origin,
                        },
                    });
                }
            });
    }, [dispatch, navigate, trimmed, origin, from, to, isOrderTypeExcluded]);

    const debouncedSearch = useDebounce(handleSearch, 350);

    useEffect(() => {
        if (trimmed.length >= 1) debouncedSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trimmed, from, to]);

    const handlePaste = async () => {
        try {
            const text = await navigator.clipboard.readText();
            if (text) {
                setQuery(text);
            }
        } catch (error) {
            console.error('Failed to read clipboard contents:', error);
        }
    };

    const enterClick = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleClear = () => {
        setQuery('');
        dispatch(clearSearchOrders());
        if (inputRef.current) {
            inputRef.current.value = '';
            inputRef.current.focus();
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setQuery(value);
    };

    return (
        <Flex column $grow={1}>
            <SearchInput
                inputRef={inputRef}
                containerRef={inputContainerRef}
                onSearch={handleChange}
                onPaste={handlePaste}
                onKeyPress={enterClick}
                onClear={handleClear}
                onIconClick={handleSearch}
                placeholder={placeholder}
                searchString={query}
            />
        </Flex>
    );
};

export default Search;
